

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { getSocket } from "../ChatPage/socket";

import Layout from "../../components/Layout";
import Loader from "../../components/Loader";
import { resolveAnyStaticUrl } from "../../urlConfig";
import {
    getCatalogItem,
    getCatalogProductCategories,
    getCatalogReviewBootstrap,
    getOldForm,
    initializeCatalogReview,
    listCatalogItems,
    listOldForms,
    mergeCatalogDuplicates,
    removeCatalogDuplicate,
} from "./catalogReviewApi";
import { useCatalogReviewSocket } from "./useCatalogReviewSocket";
import CatalogExtensionPanel from "./CatalogExtensionPanel";
import { useCatalogPricingReview } from "./CatalogPricingReviewProvider";
import {
    useAdminRealtimePanel,
    useAdminRealtimePanelPublisher,
} from "../Presence/AdminRealtimePanelProvider";

const EMPTY_PAGINATION = {
    page: 1,
    limit: 60,
    total: 0,
    totalPages: 1,
};

const ALL_CATEGORIES_ID = "__all__";

const normalizeCategoryIdentity = (value) => String(value || "")
    .trim()
    .toLocaleLowerCase("ru-RU")
    .replace(/ё/g, "е");

const sortProductCategories = (categories = []) => (
    [...(Array.isArray(categories) ? categories : [])].sort((left, right) => {
        const countDifference = Number(right?.productCount || 0)
            - Number(left?.productCount || 0);

        if (countDifference !== 0) return countDifference;

        return String(left?.name || "").localeCompare(
            String(right?.name || ""),
            "ru"
        );
    })
);

const isDefaultProductCategory = (category = {}) => {
    const identity = normalizeCategoryIdentity(
        `${category?.name || ""} ${category?.slug || ""}`
    );

    return (
        identity.includes("памятник")
        && identity.includes("ритуаль")
    ) || (
        identity.includes("pamyat")
        && identity.includes("ritual")
    );
};

const getCategoryInitials = (category = {}) => {
    const words = String(category?.name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    if (!words.length) return "К";

    return words
        .slice(0, 2)
        .map((word) => word.charAt(0).toLocaleUpperCase("ru-RU"))
        .join("");
};

const TABS = [
    {
        value: "matching",
        label: "Сопоставление форм",
        description: "Сверка старого каталога с формами выбранной категории.",
    },
    {
        value: "import",
        label: "Товары, 3D и цены",
        description: "Товары, размеры 3D, цены и проблемы только выбранного набора.",
    },
];

const categoryFilterId = (value) => (
    value && value !== ALL_CATEGORIES_ID ? String(value) : ""
);

const buildCategoryWorkspacePatch = (categoryId, oldFilters = {}) => ({
    importDraft: {
        categoryId: String(categoryId || ""),
        catalogPageNumber: 0,
        galleryPurchasedNumber: null,
        galleryScopeKey: "",
        galleryImageIndex: 0,
    },
    oldNumber: null,
    selectedPurchasedNumber: null,
    oldFilters: { ...oldFilters, page: 1 },
    matchingDraft: {
        oldNumber: null,
        selectedPurchasedNumber: null,
        matchStatus: "unchecked",
        comment: "",
    },
    reviewModal: {
        open: false,
        purchasedNumber: null,
        view: "pricing",
        queueNumbers: [],
    },
    compare: { open: false },
});

const normalizeCatalogTab = (tab) => {
    if (tab === "pricing") return "import";
    return ["matching", "import"].includes(tab) ? tab : "matching";
};

const REVIEW_LABELS = {
    unchecked: "Не проверено",
    approved: "Одобрено",
    edited: "Исправлено",
    manual: "Ручная проверка",
    "source-confirmed": "Подтверждено ведомостью",
};

const MATCH_LABELS = {
    unchecked: "Не проверено",
    confirmed: "Точное совпадение",
    similar: "Только похожая",
    none: "Аналога нет",
};

const formatMoney = (value) => Number.isFinite(Number(value))
    ? `${new Intl.NumberFormat("ru-RU").format(Number(value))} ₽`
    : "—";

const formatSize = (dimensions = {}) => {
    const { height, width, thickness } = dimensions || {};
    if (![height, width, thickness].every((value) => Number(value) > 0)) return "—";
    return `${height}×${width}×${thickness} мм`;
};

const imageUrl = (path) => path ? resolveAnyStaticUrl(path) : "";

const getErrorMessage = (error) => (
    error?.response?.data?.message
    || error?.message
    || "Произошла ошибка"
);

const CatalogImage = ({ path, alt, className = "" }) => {
    if (!path) {
        return <div className="catalogReview__imagePlaceholder">Нет изображения</div>;
    }

    return (
        <img
            className={className}
            src={imageUrl(path)}
            alt={alt}
            loading="lazy"
        />
    );
};

const CategoryThumb = ({ category = null, all = false }) => {
    const path = category?.categoryImage || "";

    return (
        <span className={`catalogReview__categoryThumb ${all ? "is-all" : ""}`}>
            {path ? (
                <img
                    src={imageUrl(path)}
                    alt={category?.name || "Категория"}
                    loading="lazy"
                />
            ) : (
                <b>{all ? "ВСЕ" : getCategoryInitials(category)}</b>
            )}
        </span>
    );
};

const StatusBadge = ({ status, type = "review" }) => {
    const label = type === "match"
        ? MATCH_LABELS[status] || status
        : REVIEW_LABELS[status] || status;

    return (
        <span className={`catalogReview__status catalogReview__status--${status || "unchecked"}`}>
            {label}
        </span>
    );
};

const CompareModal = ({
    oldForm,
    purchasedItem,
    mirroredDefault,
    sharedView,
    onViewChange,
    onClose,
    onSelect,
}) => {
    const [opacity, setOpacity] = useState(50);
    const [mirrored, setMirrored] = useState(mirroredDefault === true);
    const [shiftX, setShiftX] = useState(0);
    const [shiftY, setShiftY] = useState(0);

    useEffect(() => {
        setMirrored(sharedView?.mirrored ?? mirroredDefault === true);
        setOpacity(Number(sharedView?.opacity ?? 50));
        setShiftX(Number(sharedView?.shiftX ?? 0));
        setShiftY(Number(sharedView?.shiftY ?? 0));
    }, [
        oldForm?.oldNumber,
        purchasedItem?.purchasedNumber,
        mirroredDefault,
        sharedView?.mirrored,
        sharedView?.opacity,
        sharedView?.shiftX,
        sharedView?.shiftY,
    ]);

    const update = (patch) => onViewChange?.(patch);

    if (!oldForm || !purchasedItem) return null;

    return (
        <div className="catalogReview__modalBackdrop" onMouseDown={onClose}>
            <div className="catalogReview__modal" data-catalog-scroll-id="compare-modal" data-catalog-sync-scope={`compare-${oldForm.oldNumber}-${purchasedItem.purchasedNumber}`} onMouseDown={(event) => event.stopPropagation()}>
                <div className="catalogReview__modalHead">
                    <div>
                        <h2>Сравнение №{oldForm.oldNumber} ↔ №{purchasedItem.purchasedNumber}</h2>
                        <p>Все участники видят одинаковое зеркало, прозрачность и сдвиг.</p>
                    </div>
                    <button type="button" onClick={onClose}>Закрыть</button>
                </div>

                <div className="catalogReview__compareGrid">
                    <div>
                        <span>Старый каталог №{oldForm.oldNumber}</span>
                        <CatalogImage path={oldForm.imagePath} alt={`Старая форма ${oldForm.oldNumber}`} />
                    </div>
                    <div>
                        <span>Купленный каталог №{purchasedItem.purchasedNumber}</span>
                        <CatalogImage
                            path={purchasedItem.imagePath}
                            alt={`Купленная форма ${purchasedItem.purchasedNumber}`}
                            className={mirrored ? "is-mirrored" : ""}
                        />
                    </div>
                </div>

                <div className="catalogReview__compareControls">
                    <label>
                        <span>Прозрачность: {opacity}%</span>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={opacity}
                            onChange={(event) => {
                                const value = Number(event.target.value);
                                setOpacity(value);
                                update({ opacity: value });
                            }}
                        />
                    </label>

                    <label className="catalogReview__checkbox">
                        <input
                            type="checkbox"
                            checked={mirrored}
                            onChange={(event) => {
                                setMirrored(event.target.checked);
                                update({ mirrored: event.target.checked });
                            }}
                        />
                        <span>Отразить зеркально</span>
                    </label>

                    <label>
                        <span>Сдвиг X: {shiftX}px</span>
                        <input
                            type="range"
                            min="-120"
                            max="120"
                            value={shiftX}
                            onChange={(event) => {
                                const value = Number(event.target.value);
                                setShiftX(value);
                                update({ shiftX: value });
                            }}
                        />
                    </label>

                    <label>
                        <span>Сдвиг Y: {shiftY}px</span>
                        <input
                            type="range"
                            min="-120"
                            max="120"
                            value={shiftY}
                            onChange={(event) => {
                                const value = Number(event.target.value);
                                setShiftY(value);
                                update({ shiftY: value });
                            }}
                        />
                    </label>
                </div>

                <div className="catalogReview__overlay">
                    <CatalogImage path={oldForm.imagePath} alt="Старая форма" />
                    <CatalogImage
                        path={purchasedItem.imagePath}
                        alt="Купленная форма"
                        className={mirrored ? "is-mirrored" : ""}
                    />
                    <style>
                        {`
                            .catalogReview__overlay img:last-child {
                                opacity: ${opacity / 100};
                                transform: translate(${shiftX}px, ${shiftY}px) ${mirrored ? "scaleX(-1)" : ""};
                            }
                        `}
                    </style>
                </div>

                <div className="catalogReview__modalActions">
                    <button type="button" className="is-primary" onClick={() => onSelect(purchasedItem)}>
                        Выбрать №{purchasedItem.purchasedNumber}
                    </button>
                </div>
            </div>
        </div>
    );
};

const MatchingWorkspace = ({
    bootstrap,
    oldList,
    oldPagination,
    oldFilters,
    setOldFilters,
    currentOld,
    purchasedCandidates,
    selectedPurchased,
    loading,
    onSelectOld,
    onLoadOldPage,
    onSelectPurchased,
    onSaveMatch,
    onMergeDuplicate,
    onRemoveDuplicate,
    duplicateInput,
    setDuplicateInput,
    compare,
    sharedDraft,
    remoteDraftPatch,
    sharedCompare,
    onDraftChange,
    onOpenCompare,
    onCloseCompare,
    onCompareViewChange,
}) => {
    const [comment, setComment] = useState("");
    const [status, setStatus] = useState("unchecked");
    const [manualPurchased, setManualPurchased] = useState("");
    const localEditingFieldsRef = useRef(new Set());

    useEffect(() => {
        const item = currentOld?.item;
        const isShared = Number(sharedDraft?.oldNumber) === Number(item?.oldNumber);

        setComment(isShared ? sharedDraft?.comment || "" : item?.comment || "");
        setStatus(isShared ? sharedDraft?.matchStatus || "unchecked" : item?.matchStatus || "unchecked");
        setManualPurchased(
            isShared
                ? sharedDraft?.selectedPurchasedNumber || ""
                : item?.selectedPurchasedNumber || ""
        );
    }, [
        currentOld?.item?._id,
        currentOld?.item?.revision,
        sharedDraft?.oldNumber,
        sharedDraft?.selectedPurchasedNumber,
        sharedDraft?.matchStatus,
        sharedDraft?.comment,
    ]);

    const item = currentOld?.item;
    const duplicateGroup = currentOld?.duplicateGroup;
    const groupForms = currentOld?.groupForms || [];

    useEffect(() => {
        if (
            remoteDraftPatch?.scope !== "matching"
            || Number(remoteDraftPatch?.entityNumber) !== Number(item?.oldNumber)
        ) {
            return;
        }

        const field = remoteDraftPatch.field;
        if (localEditingFieldsRef.current.has(field)) return;

        if (field === "selectedPurchasedNumber") {
            setManualPurchased(remoteDraftPatch.value ?? "");
        } else if (field === "matchStatus") {
            setStatus(remoteDraftPatch.value || "unchecked");
        } else if (field === "comment") {
            setComment(remoteDraftPatch.value || "");
        }
    }, [
        remoteDraftPatch,
        item?.oldNumber,
    ]);

    useEffect(() => {
        localEditingFieldsRef.current.clear();
    }, [item?.oldNumber]);

    const fieldProps = (name) => ({
        onFocus: () => localEditingFieldsRef.current.add(name),
        onBlur: () => localEditingFieldsRef.current.delete(name),
    });

    return (
        <div className="catalogReview__matching">
            <aside className="catalogReview__queuePanel">
                <div className="catalogReview__queueHead">
                    <h3>Старые формы</h3>
                    <span>{oldPagination.total}</span>
                </div>

                <div className="catalogReview__queueFilters">
                    <input
                        type="number"
                        min="1"
                        max="239"
                        placeholder="Номер"
                        value={oldFilters.search}
                        onChange={(event) => setOldFilters((prev) => ({
                            ...prev,
                            search: event.target.value,
                            page: 1,
                        }))}
                    />
                    <select
                        value={oldFilters.status}
                        onChange={(event) => setOldFilters((prev) => ({
                            ...prev,
                            status: event.target.value,
                            page: 1,
                        }))}
                    >
                        <option value="all">Любой статус</option>
                        <option value="unchecked">Не проверено</option>
                        <option value="confirmed">Точное совпадение</option>
                        <option value="similar">Только похожая</option>
                        <option value="none">Аналога нет</option>
                    </select>
                    <select
                        value={oldFilters.duplicate}
                        onChange={(event) => setOldFilters((prev) => ({
                            ...prev,
                            duplicate: event.target.value,
                            page: 1,
                        }))}
                    >
                        <option value="all">Все формы</option>
                        <option value="yes">Только дубли</option>
                        <option value="no">Без дублей</option>
                    </select>
                </div>

                <div className="catalogReview__queueList" data-catalog-scroll-id="old-queue">
                    {oldList.map((oldForm) => (
                        <button
                            type="button"
                            className={`catalogReview__queueRow ${
                                oldForm.oldNumber === item?.oldNumber ? "is-active" : ""
                            }`}
                            key={oldForm.oldNumber}
                            data-catalog-sync-scope={`old-row-${oldForm.oldNumber}`}
                            onClick={() => onSelectOld(oldForm.oldNumber)}
                        >
                            <CatalogImage path={oldForm.imagePath} alt={`Форма ${oldForm.oldNumber}`} />
                            <div>
                                <strong>№{oldForm.oldNumber}</strong>
                                <span>{oldForm.sourceSize || "Размер не указан"}</span>
                                <StatusBadge status={oldForm.matchStatus} type="match" />
                            </div>
                        </button>
                    ))}
                </div>

                <div className="catalogReview__pager">
                    <button
                        type="button"
                        disabled={oldPagination.page <= 1}
                        onClick={() => onLoadOldPage(oldPagination.page - 1)}
                    >
                        ←
                    </button>
                    <span>{oldPagination.page} / {oldPagination.totalPages}</span>
                    <button
                        type="button"
                        disabled={oldPagination.page >= oldPagination.totalPages}
                        onClick={() => onLoadOldPage(oldPagination.page + 1)}
                    >
                        →
                    </button>
                </div>
            </aside>

            <section className="catalogReview__panel">
                {!item ? (
                    <div className="catalogReview__empty">Выбери форму слева.</div>
                ) : (
                    <>
                        <div className="catalogReview__panelHead">
                            <div>
                                <h2>Старый каталог №{item.oldNumber}</h2>
                                <p>{item.sourceSize || "Размер не указан"} · {formatMoney(item.sourceMillingPrice)}</p>
                            </div>
                            <StatusBadge status={item.matchStatus} type="match" />
                        </div>

                        <div className="catalogReview__heroImage">
                            <CatalogImage path={item.imagePath} alt={`Старая форма ${item.oldNumber}`} />
                        </div>

                        {item.priceNote ? (
                            <div className="catalogReview__warning">{item.priceNote}</div>
                        ) : null}

                        <div className="catalogReview__matchForm">
                            <label>
                                <span>Номер купленной формы</span>
                                <div>
                                    <input
                                        type="number"
                                        min="1"
                                        max={bootstrap?.project?.settings?.maxPurchasedNumber || 1112}
                                        value={manualPurchased}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            setManualPurchased(value);
                                            onDraftChange?.("selectedPurchasedNumber", value);
                                        }}
                                        {...fieldProps("selectedPurchasedNumber")}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => onSelectPurchased(Number(manualPurchased))}
                                    >
                                        Открыть
                                    </button>
                                </div>
                            </label>

                            <label>
                                <span>Результат проверки</span>
                                <select
                                    value={status}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setStatus(value);
                                        onDraftChange?.("matchStatus", value);
                                    }}
                                    {...fieldProps("matchStatus")}
                                >
                                    <option value="unchecked">Не проверено</option>
                                    <option value="confirmed">Точное совпадение</option>
                                    <option value="similar">Только похожая форма</option>
                                    <option value="none">Точного аналога нет</option>
                                </select>
                            </label>

                            <label className="is-wide">
                                <span>Комментарий</span>
                                <textarea
                                    value={comment}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setComment(value);
                                        onDraftChange?.("comment", value);
                                    }}
                                    {...fieldProps("comment")}
                                    placeholder="Что совпадает или почему форма не подходит"
                                />
                            </label>

                            <button
                                type="button"
                                className="catalogReview__primaryAction"
                                disabled={loading}
                                onClick={() => onSaveMatch({
                                    selectedPurchasedNumber: status === "none"
                                        ? null
                                        : Number(manualPurchased || selectedPurchased?.purchasedNumber || 0) || null,
                                    matchStatus: status,
                                    comment,
                                })}
                            >
                                Сохранить сопоставление
                            </button>
                        </div>

                        <div className="catalogReview__duplicateBox">
                            <div className="catalogReview__duplicateHead">
                                <div>
                                    <strong>Группа одинаковых форм</strong>
                                    <span>
                                        {duplicateGroup
                                            ? duplicateGroup.members.map((number) => `№${number}`).join(" = ")
                                            : "Дубль пока не отмечен"}
                                    </span>
                                </div>
                            </div>

                            {groupForms.length ? (
                                <div className="catalogReview__duplicateMembers">
                                    {groupForms.map((form) => (
                                        <button
                                            type="button"
                                            key={form.oldNumber}
                                            data-catalog-sync-scope={`duplicate-${form.oldNumber}`}
                                            onClick={() => onSelectOld(form.oldNumber)}
                                        >
                                            <CatalogImage path={form.imagePath} alt={`Дубль ${form.oldNumber}`} />
                                            <strong>№{form.oldNumber}</strong>
                                            <span>{form.sourceSize}</span>
                                            <span>{formatMoney(form.sourceMillingPrice)}</span>
                                        </button>
                                    ))}
                                </div>
                            ) : null}

                            <div className="catalogReview__duplicateActions">
                                <input
                                    type="number"
                                    min="1"
                                    max="239"
                                    value={duplicateInput}
                                    disabled={loading}
                                    onChange={(event) => setDuplicateInput(event.target.value)}
                                    placeholder="Номер такой же формы"
                                />
                                <button type="button" disabled={loading} onClick={onMergeDuplicate}>
                                    Объединить как одну модель
                                </button>
                                {duplicateGroup ? (
                                    <button type="button" className="is-danger" disabled={loading} onClick={onRemoveDuplicate}>
                                        Убрать №{item.oldNumber} из группы
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    </>
                )}
            </section>

            <section className="catalogReview__panel">
                <div className="catalogReview__panelHead">
                    <div>
                        <h2>Купленный каталог</h2>
                        <p>Контур, зеркало и сдвиг — только подсказка. Решение подтверждается вручную.</p>
                    </div>
                </div>

                {selectedPurchased ? (
                    <div className="catalogReview__selectedPurchased">
                        <div className="catalogReview__heroImage">
                            <CatalogImage
                                path={selectedPurchased.imagePath}
                                alt={`Купленная форма ${selectedPurchased.purchasedNumber}`}
                            />
                        </div>
                        <div>
                            <strong>№{selectedPurchased.purchasedNumber}</strong>
                            <span>{formatSize(selectedPurchased.pricingDraft?.baseDimensions)}</span>
                            <span>{formatMoney(selectedPurchased.pricingDraft?.basePrice)}</span>
                        </div>
                    </div>
                ) : (
                    <div className="catalogReview__empty">Выбери кандидата.</div>
                )}

                <div className="catalogReview__candidateGrid" data-catalog-scroll-id="matching-candidates">
                    {purchasedCandidates.map((candidate) => (
                        <article
                            className={`catalogReview__candidate ${
                                candidate.purchasedNumber === selectedPurchased?.purchasedNumber
                                    ? "is-selected"
                                    : ""
                            }`}
                            key={candidate.purchasedNumber}
                            data-catalog-sync-scope={`candidate-${candidate.purchasedNumber}`}
                        >
                            <CatalogImage
                                path={candidate.imagePath}
                                alt={`Кандидат ${candidate.purchasedNumber}`}
                            />
                            <div>
                                <strong>№{candidate.purchasedNumber}</strong>
                                <span>
                                    {candidate._suggestion?.contourScore
                                        ? `Контур ${Math.round(candidate._suggestion.contourScore * 100)}%`
                                        : "Весь каталог"}
                                </span>
                                {candidate._suggestion?.mirrored ? <em>Зеркально</em> : null}
                            </div>
                            <div className="catalogReview__candidateActions">
                                <button
                                    type="button"
                                    onClick={() => onOpenCompare?.({
                                        oldForm: item,
                                        purchasedItem: candidate,
                                        mirroredDefault: candidate._suggestion?.mirrored === true,
                                    })}
                                >
                                    Сравнить
                                </button>
                                <button type="button" onClick={() => onSelectPurchased(candidate.purchasedNumber)}>
                                    Выбрать
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            {compare ? (
                <CompareModal
                    {...compare}
                    sharedView={sharedCompare}
                    onViewChange={onCompareViewChange}
                    onClose={onCloseCompare}
                    onSelect={(candidate) => {
                        onSelectPurchased(candidate.purchasedNumber);
                        setManualPurchased(candidate.purchasedNumber);
                        onDraftChange?.("selectedPurchasedNumber", candidate.purchasedNumber);
                        onCloseCompare?.();
                    }}
                />
            ) : null}
        </div>
    );
};

const CatalogReviewPage = () => {
    const {
        realtimeEnabled,
        uiBridgeStatus,
        source: realtimePanelSource,
    } = useAdminRealtimePanel();
    const [activeTab, setActiveTab] = useState("matching");
    const [bootstrap, setBootstrap] = useState(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null);

    const [oldList, setOldList] = useState([]);
    const [oldPagination, setOldPagination] = useState(EMPTY_PAGINATION);
    const [oldFilters, setOldFilters] = useState({
        page: 1,
        limit: 60,
        status: "all",
        duplicate: "all",
        search: "",
    });
    const [currentOld, setCurrentOld] = useState(null);

    const [productCategories, setProductCategories] = useState([]);
    const [selectedProductCategoryId, setSelectedProductCategoryId] = useState("");
    const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
    const [catalogExtensionRefreshToken, setCatalogExtensionRefreshToken] = useState(0);

    const [purchasedCandidates, setPurchasedCandidates] = useState([]);
    const [selectedPurchased, setSelectedPurchased] = useState(null);
    const [duplicateInput, setDuplicateInput] = useState("");
    const [compare, setCompare] = useState(null);
    const remoteWorkspaceRef = useRef(false);
    const defaultCategoryAppliedRef = useRef(false);
    const [remoteDraftPatch, setRemoteDraftPatch] = useState(null);
    const lastWorkspaceViewRef = useRef({});
    const publishWorkspaceRef = useRef(() => Promise.resolve(null));
    const openPricingFromCatalogRef = useRef(null);
    const {
        isOpen: pricingReviewOpen,
        currentNumber: pricingReviewNumber,
        openCatalogPricingReview,
        syncCatalogPricingReview,
        applyRemoteCatalogPricingPatch,
        applyCatalogPricingItemUpdate,
        syncCatalogPricingRealtime,
    } = useCatalogPricingReview();

    const refreshBootstrap = useCallback(async () => {
        const result = await getCatalogReviewBootstrap();
        setBootstrap(result);
        return result;
    }, []);

    const loadProductCategories = useCallback(async () => {
        const result = await getCatalogProductCategories();
        const categories = sortProductCategories(result.categories || []);

        setProductCategories(categories);
        return categories;
    }, []);

    const loadOldList = useCallback(async (override = {}) => {
        const params = { ...oldFilters, ...override };
        const result = await listOldForms(params);
        setOldList(result.items || []);
        setOldPagination(result.pagination || EMPTY_PAGINATION);
        return result;
    }, [oldFilters]);


    const loadOldDetail = useCallback(async (oldNumber) => {
        const result = await getOldForm(oldNumber);
        setCurrentOld(result);

        const suggestions = result.item?.suggestions || [];
        const numbers = suggestions.slice(0, 60).map((row) => row.purchasedNumber);
        if (result.item?.selectedPurchasedNumber) numbers.unshift(result.item.selectedPurchasedNumber);

        const uniqueNumbers = Array.from(new Set(numbers.filter(Boolean)));
        if (uniqueNumbers.length) {
            const candidatesResult = await listCatalogItems({
                numbers: uniqueNumbers.join(","),
                limit: 120,
                page: 1,
                categoryId: categoryFilterId(selectedProductCategoryId),
            });

            const suggestionByNumber = new Map(
                suggestions.map((row) => [Number(row.purchasedNumber), row])
            );

            const sorted = (candidatesResult.items || [])
                .map((row) => ({
                    ...row,
                    _suggestion: suggestionByNumber.get(Number(row.purchasedNumber)) || null,
                }))
                .sort((a, b) => {
                    const aIndex = uniqueNumbers.indexOf(Number(a.purchasedNumber));
                    const bIndex = uniqueNumbers.indexOf(Number(b.purchasedNumber));
                    return aIndex - bIndex;
                });

            setPurchasedCandidates(sorted);

            const selected = sorted.find((row) => (
                Number(row.purchasedNumber) === Number(result.item.selectedPurchasedNumber)
            ));
            setSelectedPurchased(selected || null);
        } else {
            setPurchasedCandidates([]);
            setSelectedPurchased(null);
        }

        return result;
    }, [selectedProductCategoryId]);


    const showStatus = useCallback((type, text) => {
        setStatus({ type, text });
        window.setTimeout(() => setStatus(null), 5000);
    }, []);

    const onOldUpdated = useCallback((payload = {}) => {
        const item = payload.item;
        if (!item) return;

        setOldList((prev) => prev.map((row) => (
            row.oldNumber === item.oldNumber ? item : row
        )));

        setCurrentOld((prev) => (
            prev?.item?.oldNumber === item.oldNumber
                ? { ...prev, item }
                : prev
        ));

        loadOldList().catch(() => {});
        refreshBootstrap().catch(() => {});
    }, [loadOldList, refreshBootstrap]);

    const onItemUpdated = useCallback((payload = {}) => {
        const item = payload.item;
        if (!item) return;

        setPurchasedCandidates((previous) => previous.map((row) => (
            row.purchasedNumber === item.purchasedNumber
                ? { ...row, ...item }
                : row
        )));

        setSelectedPurchased((previous) => (
            previous?.purchasedNumber === item.purchasedNumber
                ? { ...previous, ...item }
                : previous
        ));

        applyCatalogPricingItemUpdate(item);

        const itemCategoryId = String(item?.productLink?.categoryId || '');
        const activeCategoryId = categoryFilterId(selectedProductCategoryId);
        if (
            selectedProductCategoryId === ALL_CATEGORIES_ID
            || !itemCategoryId
            || itemCategoryId === activeCategoryId
        ) {
            setCatalogExtensionRefreshToken(Date.now());
        }

    }, [
        applyCatalogPricingItemUpdate,
        selectedProductCategoryId,
    ]);

    const onProjectUpdated = useCallback((payload = {}) => {
        if (!payload.project) return;
        setBootstrap((prev) => ({
            ...prev,
            project: payload.project,
        }));
        if (currentOld?.item?.oldNumber) {
            loadOldDetail(currentOld.item.oldNumber).catch(() => {});
        }
    }, [currentOld?.item?.oldNumber, loadOldDetail]);

    const applyCategoryContext = useCallback((nextCategoryId) => {
        const normalized = String(nextCategoryId || "");

        setSelectedProductCategoryId(normalized);
        setOldList([]);
        setOldPagination(EMPTY_PAGINATION);
        setCurrentOld(null);
        setPurchasedCandidates([]);
        setSelectedPurchased(null);
        setDuplicateInput("");
        setCompare(null);
        setRemoteDraftPatch(null);
        setOldFilters((previous) => ({ ...previous, page: 1 }));
        setCatalogExtensionRefreshToken(Date.now());
        syncCatalogPricingReview({ open: false });
    }, [syncCatalogPricingReview]);


    const onWorkspaceChanged = useCallback((nextWorkspace, meta = {}) => {
        if (!nextWorkspace) return;

        const previous = lastWorkspaceViewRef.current || {};
        lastWorkspaceViewRef.current = nextWorkspace;

        if (!meta.remote && !meta.initial) return;
        remoteWorkspaceRef.current = true;

        if (nextWorkspace.activeTab && nextWorkspace.activeTab !== previous.activeTab) {
            setActiveTab(normalizeCatalogTab(nextWorkspace.activeTab));
        }

        const oldFiltersChanged = JSON.stringify(nextWorkspace.oldFilters || {})
            !== JSON.stringify(previous.oldFilters || {});

        if (oldFiltersChanged && nextWorkspace.oldFilters) {
            setOldFilters((current) => ({ ...current, ...nextWorkspace.oldFilters }));
        }

        const nextCategoryId = String(nextWorkspace.importDraft?.categoryId || "");
        const previousCategoryId = String(previous.importDraft?.categoryId || "");

        /*
         * Выбранная в совместной комнате категория имеет приоритет.
         * Первый подключившийся пользователь больше не очищает сохранённый
         * рабочий набор: это предотвращает мигание экрана выбора категорий.
         */
        if (
            nextCategoryId
            && (
                nextCategoryId !== previousCategoryId
                || nextCategoryId !== selectedProductCategoryId
            )
        ) {
            defaultCategoryAppliedRef.current = true;
            applyCategoryContext(nextCategoryId);
        }

        const contextAvailable = Boolean(
            selectedProductCategoryId || nextCategoryId
        );

        if (!contextAvailable) {
            window.setTimeout(() => {
                remoteWorkspaceRef.current = false;
            }, 0);
            return;
        }

        if (nextWorkspace.oldNumber
            && Number(nextWorkspace.oldNumber) !== Number(previous.oldNumber)) {
            loadOldDetail(nextWorkspace.oldNumber).catch(() => {});
        }
        if (nextWorkspace.selectedPurchasedNumber
            && Number(nextWorkspace.selectedPurchasedNumber)
                !== Number(previous.selectedPurchasedNumber)) {
            getCatalogItem(nextWorkspace.selectedPurchasedNumber)
                .then((result) => setSelectedPurchased(result.item || null))
                .catch(() => {});
        }

        const nextCompareKey = nextWorkspace.compare?.open
            ? `${nextWorkspace.compare.oldNumber}:${nextWorkspace.compare.purchasedNumber}`
            : '';
        const previousCompareKey = previous.compare?.open
            ? `${previous.compare.oldNumber}:${previous.compare.purchasedNumber}`
            : '';

        if (nextCompareKey && nextCompareKey !== previousCompareKey) {
            Promise.all([
                getOldForm(nextWorkspace.compare.oldNumber),
                getCatalogItem(nextWorkspace.compare.purchasedNumber),
            ]).then(([oldResult, purchasedResult]) => {
                setCompare({
                    oldForm: oldResult.item,
                    purchasedItem: purchasedResult.item,
                    mirroredDefault: nextWorkspace.compare.mirrored === true,
                });
            }).catch(() => {});
        } else if (!nextWorkspace.compare?.open && previous.compare?.open) {
            setCompare(null);
        }

        const nextReviewModal = nextWorkspace.reviewModal || {};
        const previousReviewModal = previous.reviewModal || {};
        const nextReviewKey = nextReviewModal.open
            ? [
                nextReviewModal.purchasedNumber,
                nextReviewModal.view,
                ...(nextReviewModal.queueNumbers || []),
            ].join(':')
            : '';
        const previousReviewKey = previousReviewModal.open
            ? [
                previousReviewModal.purchasedNumber,
                previousReviewModal.view,
                ...(previousReviewModal.queueNumbers || []),
            ].join(':')
            : '';

        if (
            nextReviewModal.open
            && nextReviewModal.purchasedNumber
            && nextReviewKey !== previousReviewKey
        ) {
            openPricingFromCatalogRef.current?.(
                nextReviewModal.purchasedNumber,
                {
                    remoteSync: true,
                    initialView: nextReviewModal.view,
                    queueNumbers: nextReviewModal.queueNumbers || [],
                    initialDraft: (
                        Number(nextWorkspace.pricingDraft?.purchasedNumber || 0)
                        === Number(nextReviewModal.purchasedNumber)
                    )
                        ? nextWorkspace.pricingDraft
                        : null,
                }
            );
        } else if (
            !nextReviewModal.open
            && previousReviewModal.open
        ) {
            syncCatalogPricingReview({ open: false });
        }

        window.setTimeout(() => {
            remoteWorkspaceRef.current = false;
        }, 0);
    }, [
        applyCategoryContext,
        loadOldDetail,
        selectedProductCategoryId,
        syncCatalogPricingReview,
    ]);

    const onRemoteDraftPatch = useCallback((payload = {}) => {
        const nextPatch = {
            ...payload,
            receivedAt: Date.now(),
        };

        setRemoteDraftPatch(nextPatch);
        applyRemoteCatalogPricingPatch(nextPatch);
    }, [applyRemoteCatalogPricingPatch]);


    const onCatalogDataChanged = useCallback((payload = {}) => {
        const refreshToken = Number(payload.refreshToken || Date.now());
        const changedCategoryId = String(payload.categoryId || '');
        const activeCategoryId = categoryFilterId(selectedProductCategoryId);
        const relevantToCurrentCategory = (
            selectedProductCategoryId === ALL_CATEGORIES_ID
            || !changedCategoryId
            || changedCategoryId === activeCategoryId
        );

        if (relevantToCurrentCategory) {
            setCatalogExtensionRefreshToken(refreshToken);
        }

        if (
            payload.kind === 'duplicates'
            && currentOld?.item?.oldNumber
        ) {
            loadOldDetail(currentOld.item.oldNumber).catch(() => {});
        }
    }, [
        currentOld?.item?.oldNumber,
        loadOldDetail,
        selectedProductCategoryId,
    ]);

    const focusType = pricingReviewOpen ? "pricing" : activeTab;
    const focusNumber = pricingReviewOpen
        ? pricingReviewNumber
        : (
            activeTab === "matching"
                ? currentOld?.item?.oldNumber
                : null
        );

    const {
        workspace,
        operations,
        updateOld,
        updateItem,
        updateWorkspace,
        notifyDataChanged,
        publishOperation,
        sendDraftPatch,
    } = useCatalogReviewSocket({
        enabled: realtimeEnabled,
        onOldUpdated,
        onItemUpdated,
        onProjectUpdated,
        onWorkspace: onWorkspaceChanged,
        onDraftPatch: onRemoteDraftPatch,
        onDataChanged: onCatalogDataChanged,
    });

    const genericRealtimeActive = Boolean(
        realtimeEnabled
        && uiBridgeStatus.pageKey === 'catalog-review'
    );
    const genericRealtimeUsers = genericRealtimeActive
        && realtimePanelSource?.sourceId === 'catalog-review'
        ? (realtimePanelSource.users || [])
        : [];
    const genericRealtimeConnected = Boolean(
        genericRealtimeActive
        && realtimePanelSource?.connected
    );
    const genericRealtimeSocketId = genericRealtimeActive
        ? String(uiBridgeStatus.socketId || '')
        : '';

    useEffect(() => {
        syncCatalogPricingRealtime({
            users: genericRealtimeUsers,
            connected: genericRealtimeConnected,
            socketId: genericRealtimeSocketId,
            operations,
            workspace,
            publishOperation,
        });
    }, [
        genericRealtimeConnected,
        genericRealtimeSocketId,
        genericRealtimeUsers,
        operations,
        workspace,
        publishOperation,
        syncCatalogPricingRealtime,
    ]);


    const publishWorkspace = useCallback((patch) => {
        if (remoteWorkspaceRef.current) return Promise.resolve(null);
        return updateWorkspace(patch).catch((error) => {
            showStatus("error", getErrorMessage(error));
            return null;
        });
    }, [showStatus, updateWorkspace]);

    publishWorkspaceRef.current = publishWorkspace;


    /*
     * Узкие UI-события CatalogReview идут через уже существующий общий
     * adminRealtime channel. Domain transport CatalogReview (workspace,
     * draftPatch, pricing, open-card, pagination) здесь НЕ меняется.
     */
    const publishCatalogUiState = useCallback((stateKey, value) => {
        if (!realtimeEnabled) return false;

        const socket = getSocket();
        if (!socket?.connected) return false;

        socket.emit('adminRealtime:state', {
            token: window.localStorage.getItem('token'),
            pageKey: 'catalog-review',
            type: 'state',
            targetId: 'catalog-review-page',
            state: {
                namespace: 'catalog-review-local-ui',
                stateKey,
                action: stateKey,
                value,
            },
        });

        return true;
    }, [realtimeEnabled]);

    const setSharedCategoryPickerOpen = useCallback((nextValue) => {
        const nextOpen = nextValue === true;
        setCategoryPickerOpen(nextOpen);
        publishCatalogUiState('category-picker-open', nextOpen);
    }, [publishCatalogUiState]);

    const publishProductSeoNavigation = useCallback(() => {
        publishCatalogUiState('navigate-product-seo', {
            target: '/containerProductPage/product-seo-price',
            navigationId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        });
        // Локальный <Link> не перехватываем: он работает ровно как раньше.
    }, [publishCatalogUiState]);

    useEffect(() => {
        if (!realtimeEnabled) return undefined;

        const socket = getSocket();

        const onAdminRealtimeState = (event = {}) => {
            if (String(event.pageKey || '') !== 'catalog-review') return;
            if (event.type !== 'state') return;
            if (event.socketId && event.socketId === socket.id) return;

            const state = event.state || {};
            if (state.namespace !== 'catalog-review-local-ui') return;

            if (state.stateKey === 'category-picker-open') {
                setCategoryPickerOpen(state.value === true);
                return;
            }

            if (state.stateKey === 'navigate-product-seo') {
                const target = String(state.value?.target || '');
                if (target !== '/containerProductPage/product-seo-price') return;
                if (window.location.pathname === target) return;
                window.location.assign(target);
            }
        };

        socket.on('adminRealtime:state', onAdminRealtimeState);
        return () => socket.off('adminRealtime:state', onAdminRealtimeState);
    }, [realtimeEnabled]);

    /*
     * Поиск и пагинация — часто меняющееся semantic-состояние каталога.
     * Для них используем уже существующий sequence-deduplicated draftPatch.
     * Остальные importDraft-поля продолжают идти через canonical workspace.
     *
     * Если draftPatch временно недоступен (например, socket ещё не joined),
     * поле остаётся в workspacePatch и используется обычный workspace-канал.
     */
    const publishCatalogImportDraft = useCallback((patch = {}) => {
        if (!patch || typeof patch !== "object") {
            return Promise.resolve(null);
        }

        const workspacePatch = { ...patch };

        ["catalogPageNumber", "catalogSearch"].forEach((field) => {
            if (!Object.prototype.hasOwnProperty.call(workspacePatch, field)) {
                return;
            }

            const sequence = sendDraftPatch({
                scope: "import",
                field,
                value: workspacePatch[field],
            });

            if (sequence) {
                delete workspacePatch[field];
            }
        });

        if (!Object.keys(workspacePatch).length) {
            return Promise.resolve(null);
        }

        return publishWorkspace({
            importDraft: workspacePatch,
        });
    }, [
        publishWorkspace,
        sendDraftPatch,
    ]);

    const publishRealtimeChange = useCallback((payload = {}) => (
        notifyDataChanged({
            ...payload,
            categoryId: payload.categoryId || selectedProductCategoryId || '',
        }).catch((error) => {
            console.warn(
                'catalogReview realtime refresh error:',
                getErrorMessage(error)
            );
            return null;
        })
    ), [
        notifyDataChanged,
        selectedProductCategoryId,
    ]);


    const changeTab = useCallback((tab) => {
        const nextTab = normalizeCatalogTab(tab);
        setActiveTab(nextTab);
        publishWorkspace({ activeTab: nextTab });
    }, [publishWorkspace]);

    const setSharedOldFilters = useCallback((updater) => {
        setOldFilters((previous) => {
            const next = typeof updater === "function" ? updater(previous) : updater;
            publishWorkspace({ oldFilters: next });
            return next;
        });
    }, [publishWorkspace]);


    const changeProductCategory = useCallback((categoryId) => {
        const nextCategoryId = String(categoryId || "");

        defaultCategoryAppliedRef.current = true;
        setSharedCategoryPickerOpen(false);
        applyCategoryContext(nextCategoryId);
        publishWorkspace(
            buildCategoryWorkspacePatch(nextCategoryId, oldFilters)
        );
    }, [
        applyCategoryContext,
        oldFilters,
        publishWorkspace,
        setSharedCategoryPickerOpen,
    ]);

    useEffect(() => {
        if (
            defaultCategoryAppliedRef.current
            || selectedProductCategoryId
            || !productCategories.length
        ) {
            return;
        }

        const sharedCategoryId = String(
            workspace?.importDraft?.categoryId || ""
        );

        if (sharedCategoryId) {
            defaultCategoryAppliedRef.current = true;
            applyCategoryContext(sharedCategoryId);
            return;
        }

        /*
         * При включённых сокетах ждём первоначальный workspace, чтобы не
         * перезаписать категорию, уже выбранную другим участником комнаты.
         */
        if (realtimeEnabled && !workspace) return;

        const defaultCategory = productCategories.find(
            isDefaultProductCategory
        ) || productCategories[0];

        if (!defaultCategory?._id) return undefined;

        const applyDefaultCategory = () => {
            changeProductCategory(defaultCategory._id);
        };

        if (remoteWorkspaceRef.current) {
            const timer = window.setTimeout(applyDefaultCategory, 0);
            return () => window.clearTimeout(timer);
        }

        applyDefaultCategory();
        return undefined;
    }, [
        applyCategoryContext,
        changeProductCategory,
        productCategories,
        realtimeEnabled,
        selectedProductCategoryId,
        workspace,
    ]);



    const selectOldShared = useCallback(async (oldNumber) => {
        const result = await loadOldDetail(oldNumber);
        const item = result?.item;

        publishWorkspace({
            oldNumber,
            activeTab: "matching",
            matchingDraft: {
                oldNumber,
                selectedPurchasedNumber: item?.selectedPurchasedNumber || "",
                matchStatus: item?.matchStatus || "unchecked",
                comment: item?.comment || "",
            },
        });
    }, [loadOldDetail, publishWorkspace]);


    const updateMatchingDraft = useCallback((field, value) => {
        const oldNumber = currentOld?.item?.oldNumber || null;
        if (!oldNumber) return;

        sendDraftPatch({
            scope: "matching",
            entityNumber: oldNumber,
            field,
            value,
        });
    }, [currentOld?.item?.oldNumber, sendDraftPatch]);


    const openCompareShared = useCallback((nextCompare) => {
        setCompare(nextCompare);
        publishWorkspace({
            compare: {
                open: true,
                oldNumber: nextCompare?.oldForm?.oldNumber || null,
                purchasedNumber: nextCompare?.purchasedItem?.purchasedNumber || null,
                mirrored: nextCompare?.mirroredDefault === true,
                opacity: 50,
                shiftX: 0,
                shiftY: 0,
            },
        });
    }, [publishWorkspace]);

    const closeCompareShared = useCallback(() => {
        setCompare(null);
        publishWorkspace({ compare: { open: false } });
    }, [publishWorkspace]);

    const updateCompareView = useCallback((patch) => {
        publishWorkspace({ compare: patch });
    }, [publishWorkspace]);

    useEffect(() => {
        let mounted = true;

        const start = async () => {
            setLoading(true);

            try {
                await Promise.all([
                    refreshBootstrap(),
                    loadProductCategories(),
                ]);
            } catch (error) {
                showStatus("error", getErrorMessage(error));
            } finally {
                if (mounted) setLoading(false);
            }
        };

        start();

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (
            !bootstrap?.initialized
            || !selectedProductCategoryId
            || activeTab !== "matching"
        ) {
            return undefined;
        }

        let cancelled = false;
        const timer = window.setTimeout(async () => {
            setLoading(true);

            try {
                const result = await loadOldList();
                if (cancelled) return;

                const items = result?.items || [];
                const currentNumber = Number(currentOld?.item?.oldNumber || 0);
                const currentStillVisible = items.some(
                    (row) => Number(row.oldNumber) === currentNumber
                );

                if (!currentStillVisible && items[0]) {
                    await loadOldDetail(items[0].oldNumber);
                } else if (!items.length) {
                    setCurrentOld(null);
                    setPurchasedCandidates([]);
                    setSelectedPurchased(null);
                }
            } catch (error) {
                if (!cancelled) showStatus("error", getErrorMessage(error));
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 180);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [
        activeTab,
        bootstrap?.initialized,
        currentOld?.item?.oldNumber,
        loadOldDetail,
        loadOldList,
        oldFilters.page,
        oldFilters.status,
        oldFilters.duplicate,
        oldFilters.search,
        selectedProductCategoryId,
    ]);


    const handleInitialize = async () => {
        const operationKey = 'catalog:initialize';
        setLoading(true);
        publishOperation({ key: operationKey, running: true });
        try {
            const result = await initializeCatalogReview();
            setBootstrap(result);
            showStatus("success", result.message || "Каталог инициализирован");
            await publishRealtimeChange({
                kind: "initialize",
            });
        } catch (error) {
            showStatus("error", getErrorMessage(error));
        } finally {
            setLoading(false);
            publishOperation({ key: operationKey, running: false });
        }
    };

    const selectPurchased = async (purchasedNumber) => {
        if (!purchasedNumber) return;
        try {
            const result = await getCatalogItem(purchasedNumber);
            const next = result.item;
            const activeCategoryFilter = categoryFilterId(selectedProductCategoryId);
            const itemCategoryId = String(next?.productLink?.categoryId || "");

            if (
                activeCategoryFilter
                && itemCategoryId
                && itemCategoryId !== activeCategoryFilter
            ) {
                showStatus(
                    "error",
                    "Эта форма относится к другой категории. Переключи категорию или выбери «Все товары»."
                );
                return;
            }

            setSelectedPurchased(next);
            setPurchasedCandidates((prev) => {
                if (prev.some((row) => row.purchasedNumber === next.purchasedNumber)) return prev;
                return [{ ...next, _suggestion: null }, ...prev];
            });
            publishWorkspace({
                selectedPurchasedNumber: next.purchasedNumber,
                matchingDraft: {
                    oldNumber: currentOld?.item?.oldNumber || null,
                    selectedPurchasedNumber: next.purchasedNumber,
                },
            });
        } catch (error) {
            showStatus("error", getErrorMessage(error));
        }
    };

    const saveMatch = async (patch) => {
        if (!currentOld?.item) return;
        const operationKey = `matching:save:${currentOld.item.oldNumber}`;
        setLoading(true);
        publishOperation({ key: operationKey, running: true });
        try {
            const result = await updateOld({
                oldNumber: currentOld.item.oldNumber,
                revision: currentOld.item.revision,
                patch,
            });
            setCurrentOld((prev) => ({ ...prev, item: result.item }));
            showStatus("success", "Сопоставление сохранено");
        } catch (error) {
            if (error.latest) {
                setCurrentOld((prev) => ({ ...prev, item: error.latest }));
            }
            showStatus("error", getErrorMessage(error));
        } finally {
            setLoading(false);
            publishOperation({ key: operationKey, running: false });
        }
    };

    const mergeDuplicate = async () => {
        const oldNumber = currentOld?.item?.oldNumber;
        const targetNumber = Number(duplicateInput);
        if (!oldNumber || !targetNumber || oldNumber === targetNumber) {
            showStatus("error", "Укажи другой номер одинаковой формы");
            return;
        }

        const operationKey = `matching:merge:${oldNumber}`;
        publishOperation({ key: operationKey, running: true });

        try {
            const result = await mergeCatalogDuplicates({
                revision: bootstrap.project.revision,
                members: [oldNumber, targetNumber],
                note: `Формы №${oldNumber} и №${targetNumber} объединены вручную`,
            });
            setBootstrap((prev) => ({ ...prev, project: result.project }));
            setDuplicateInput("");
            await loadOldDetail(oldNumber);
            showStatus("success", "Формы объединены в одну модель");
            await publishRealtimeChange({
                kind: "duplicates",
                oldNumbers: [oldNumber, targetNumber],
            });
        } catch (error) {
            if (error?.response?.data?.latest) {
                setBootstrap((prev) => ({
                    ...prev,
                    project: error.response.data.latest,
                }));
            }
            showStatus("error", getErrorMessage(error));
        } finally {
            publishOperation({ key: operationKey, running: false });
        }
    };

    const removeDuplicate = async () => {
        if (!currentOld?.item) return;
        const operationKey = `matching:remove:${currentOld.item.oldNumber}`;
        publishOperation({ key: operationKey, running: true });
        try {
            const result = await removeCatalogDuplicate({
                oldNumber: currentOld.item.oldNumber,
                revision: bootstrap.project.revision,
            });
            setBootstrap((prev) => ({ ...prev, project: result.project }));
            await loadOldDetail(currentOld.item.oldNumber);
            showStatus("success", "Номер удалён из группы дублей");
            await publishRealtimeChange({
                kind: "duplicates",
                oldNumbers: [currentOld.item.oldNumber],
            });
        } catch (error) {
            showStatus("error", getErrorMessage(error));
        } finally {
            publishOperation({ key: operationKey, running: false });
        }
    };

    const refreshAllSummary = useCallback(async ({ refreshExtension = true } = {}) => {
        const operationKey = 'catalog:refresh-summary';
        publishOperation({ key: operationKey, running: true });

        try {
            await refreshBootstrap();

            if (refreshExtension) {
                setCatalogExtensionRefreshToken(Date.now());
            }

            if (activeTab === "matching" && selectedProductCategoryId) {
                await loadOldList();
            }
        } finally {
            publishOperation({ key: operationKey, running: false });
        }
    }, [
        activeTab,
        loadOldList,
        publishOperation,
        refreshBootstrap,
        selectedProductCategoryId,
    ]);

    const openPricingFromCatalog = useCallback(async (purchasedNumber, options = {}) => {
        const number = Number(purchasedNumber);
        if (!number) return;

        let queueNumbers = (options.queueNumbers || [])
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0);

        if (options.quickQueue === true && !queueNumbers.length) {
            const queueResult = await listCatalogItems({
                page: 1,
                limit: 1150,
                scope: "all",
                reviewStatus: "unchecked",
                confidence: "all",
                categoryId: categoryFilterId(selectedProductCategoryId),
                productLink: "linked",
                has3D: "all",
                hasTags: "all",
                processed: "no",
                catalogSource: "all",
                geometryStatus: "all",
                search: "",
            });

            queueNumbers = (queueResult.items || [])
                .map((row) => Number(row.purchasedNumber))
                .filter(Boolean);
        }

        if (!queueNumbers.length) {
            queueNumbers = [number];
        }

        const initialView = options.initialView === "3d" ? "3d" : "pricing";
        const openMethod = options.remoteSync
            ? syncCatalogPricingReview
            : openCatalogPricingReview;

        openMethod({
            open: true,
            purchasedNumber: number,
            queueNumbers,
            initialView,
            initialDraft: options.initialDraft || null,
            saveItem: ({ purchasedNumber: targetNumber, revision, patch }) => (
                updateItem({
                    purchasedNumber: targetNumber,
                    revision,
                    patch,
                })
            ),
            onDraftChange: ({
                purchasedNumber: activePurchasedNumber,
                field,
                value,
            }) => {
                sendDraftPatch({
                    scope: "pricing",
                    entityNumber: activePurchasedNumber,
                    field,
                    value,
                });
            },
            onDraftInitialize: ({
                purchasedNumber: activePurchasedNumber,
                draft,
            }) => publishWorkspaceRef.current({
                pricingDraft: {
                    purchasedNumber: activePurchasedNumber,
                    ...draft,
                },
            }),
            onCurrentNumberChange: (activePurchasedNumber, navigation = {}) => {
                const nextView = navigation.view === "3d"
                    ? "3d"
                    : "pricing";

                publishWorkspaceRef.current({
                    reviewModal: {
                        open: true,
                        purchasedNumber: activePurchasedNumber,
                        view: nextView,
                        queueNumbers,
                    },
                });
            },
            onSaved: async ({ item: savedItem }) => {
                if (savedItem?._id) {
                    onItemUpdated({ item: savedItem });
                }

                setCatalogExtensionRefreshToken(Date.now());
                await refreshAllSummary();
            },
            onViewChange: ({
                view,
                purchasedNumber: activePurchasedNumber,
            }) => {
                publishWorkspaceRef.current({
                    reviewModal: {
                        open: true,
                        purchasedNumber: Number(activePurchasedNumber || number),
                        view,
                        queueNumbers,
                    },
                });
            },
            onClose: () => {
                publishWorkspaceRef.current({
                    reviewModal: {
                        open: false,
                        purchasedNumber: null,
                        view: "pricing",
                        queueNumbers: [],
                    },
                });
            },
        });

        if (!options.remoteSync) {
            publishWorkspaceRef.current({
                reviewModal: {
                    open: true,
                    purchasedNumber: number,
                    view: initialView,
                    queueNumbers,
                },
                pricingDraft: {
                    purchasedNumber: number,
                },
            });
        }
    }, [
        onItemUpdated,
        openCatalogPricingReview,
        refreshAllSummary,
        selectedProductCategoryId,
        sendDraftPatch,
        syncCatalogPricingReview,
        updateItem,
    ]);

    openPricingFromCatalogRef.current = openPricingFromCatalog;

    const openCatalogProduct3D = useCallback((
        product,
        _dimensions,
        navigationProducts = null
    ) => {
        const purchasedNumber = Number(
            product?._catalogReviewPurchasedNumber || 0
        );

        if (!purchasedNumber) return;

        const queueNumbers = (Array.isArray(navigationProducts)
            ? navigationProducts
            : [])
            .map((row) => Number(row?._catalogReviewPurchasedNumber || 0))
            .filter((value) => Number.isFinite(value) && value > 0);

        openPricingFromCatalog(purchasedNumber, {
            initialView: "3d",
            queueNumbers: queueNumbers.length ? queueNumbers : [purchasedNumber],
        });
    }, [openPricingFromCatalog]);


    const activeTabInfo = useMemo(() => (
        TABS.find((tab) => tab.value === activeTab) || TABS[0]
    ), [activeTab]);

    const sortedProductCategories = useMemo(() => (
        sortProductCategories(productCategories)
    ), [productCategories]);

    const allProductsCount = useMemo(() => (
        sortedProductCategories.reduce(
            (total, category) => total + Number(category.productCount || 0),
            0
        )
    ), [sortedProductCategories]);

    const allProductsWith3D = useMemo(() => (
        sortedProductCategories.reduce(
            (total, category) => total + Number(category.with3D || 0),
            0
        )
    ), [sortedProductCategories]);

    const selectedCategory = useMemo(() => (
        sortedProductCategories.find(
            (category) => String(category._id) === String(selectedProductCategoryId)
        ) || null
    ), [sortedProductCategories, selectedProductCategoryId]);

    const hasCategoryContext = Boolean(selectedProductCategoryId);
    const selectedCategoryName = selectedProductCategoryId === ALL_CATEGORIES_ID
        ? "Все товары"
        : (selectedCategory?.name || "Категория");
    const selectedCategoryProductCount = selectedProductCategoryId === ALL_CATEGORIES_ID
        ? allProductsCount
        : Number(selectedCategory?.productCount || 0);
    const selectedCategoryWith3D = selectedProductCategoryId === ALL_CATEGORIES_ID
        ? allProductsWith3D
        : Number(selectedCategory?.with3D || 0);

    const initializeRunning = Boolean(operations['catalog:initialize']);
    const summaryRefreshing = Boolean(operations['catalog:refresh-summary']);
    const pageLoading = loading || initializeRunning;

    const realtimePanelState = useMemo(() => {
        const focusLabel = pricingReviewOpen
            ? `Проверка цены · №${pricingReviewNumber || '—'}`
            : (
                activeTab === 'matching' && currentOld?.item?.oldNumber
                    ? `Сопоставление · №${currentOld.item.oldNumber}`
                    : activeTabInfo.label
            );

        return {
            title: 'Совместная работа с каталогом',
            roomLabel: 'Каталог форм и цен фрезеровки',
            pageLabel: activeTabInfo.label,
            categoryLabel: hasCategoryContext
                ? selectedCategoryName
                : 'Категория не выбрана',
            focusType,
            focusNumber,
            focusLabel,
            /*
             * connected/users/field-locks для общей панели берутся только из
             * AdminRealtimePanelProvider -> adminRealtimeUiBridge.
             * Здесь публикуем лишь контекст страницы и доменные operations.
             */
            operationsCount: Object.keys(operations || {}).length,
        };
    }, [
        activeTab,
        activeTabInfo.label,
        currentOld?.item?.oldNumber,
        focusNumber,
        focusType,
        hasCategoryContext,
        operations,
        pricingReviewNumber,
        pricingReviewOpen,
        selectedCategoryName,
    ]);

    useAdminRealtimePanelPublisher('catalog-review', realtimePanelState);

    if (pageLoading && !bootstrap) {
        return (
            <Layout sidebar>
                <div className="catalogReview catalogReview--loading">
                    <Loader typeSize="90px" />
                </div>
            </Layout>
        );
    }

    if (!bootstrap?.initialized) {
        return (
            <Layout sidebar>
                <div className="catalogReview">
                    <div className="catalogReview__emptyProject">
                        <h1>Каталог форм ещё не заполнен</h1>
                        <p>
                            Backend ждёт изображения и файл catalog-review-seed.json
                            в папке files/catalogReview.
                        </p>
                        {status ? (
                            <div className={`catalogReview__message catalogReview__message--${status.type}`}>
                                {status.text}
                            </div>
                        ) : null}
                        <button type="button" disabled={pageLoading} onClick={handleInitialize}>
                            {pageLoading ? "Инициализация…" : "Инициализировать каталог"}
                        </button>
                        <Link
                            to="/containerProductPage/product-seo-price"
                            onClick={publishProductSeoNavigation}
                        >
                            Вернуться в «Работы + теги»
                        </Link>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout sidebar>
            <div
                className="catalogReview"
                data-catalog-sync-scope="catalog-review-page"
            >
                <header className="catalogReview__header">
                    <div>
                        <div className="catalogReview__adminMark">ADMIN</div>
                        <h1>
                            {hasCategoryContext
                                ? activeTabInfo.label
                                : "Каталог форм и цен фрезеровки"}
                        </h1>
                        <p>
                            {hasCategoryContext
                                ? activeTabInfo.description
                                : "Сначала выбери категорию. До выбора данные каталога не загружаются."}
                        </p>
                    </div>

                    <div className="catalogReview__headerActions">
                        <Link
                            to="/containerProductPage/product-seo-price"
                            onClick={publishProductSeoNavigation}
                        >
                            Работы + теги
                        </Link>
                        {hasCategoryContext ? (
                            <button
                                type="button"
                                disabled={summaryRefreshing}
                                onClick={() => refreshAllSummary()}
                            >
                                {summaryRefreshing ? "Обновление…" : "Обновить данные"}
                            </button>
                        ) : null}
                    </div>
                </header>


                {!hasCategoryContext ? (
                    <section className="catalogReview__categoryGate catalogReview__categoryGate--compact">
                        <div className="catalogReview__categoryGateHead">
                            <div>
                                <span>Рабочий набор</span>
                                <h2>Выбери категорию</h2>
                                <p>Категории отсортированы по количеству товаров.</p>
                            </div>
                        </div>

                        <div className="catalogReview__categoryGrid">
                            <button
                                type="button"
                                className="catalogReview__categoryCard catalogReview__categoryCard--all"
                                onClick={() => changeProductCategory(ALL_CATEGORIES_ID)}
                            >
                                <CategoryThumb all />
                                <span className="catalogReview__categoryCardText">
                                    <strong>Все товары</strong>
                                    <small>
                                        {allProductsCount} товаров · 3D {allProductsWith3D}
                                    </small>
                                </span>
                            </button>

                            {sortedProductCategories.map((category) => (
                                <button
                                    type="button"
                                    className="catalogReview__categoryCard"
                                    key={category._id}
                                    onClick={() => changeProductCategory(category._id)}
                                >
                                    <CategoryThumb category={category} />
                                    <span className="catalogReview__categoryCardText">
                                        <strong>{category.name}</strong>
                                        <small>
                                            {category.productCount || 0} товаров · 3D {category.with3D || 0}
                                        </small>
                                    </span>
                                </button>
                            ))}
                        </div>
                    </section>
                ) : (
                    <>
                        <section className={`catalogReview__categoryWorkspace ${categoryPickerOpen ? "is-open" : ""}`}>
                            <div className="catalogReview__categoryContext">
                                <CategoryThumb
                                    all={selectedProductCategoryId === ALL_CATEGORIES_ID}
                                    category={selectedCategory}
                                />

                                <div className="catalogReview__categoryContextMain">
                                    <span>Рабочая категория</span>
                                    <strong>{selectedCategoryName}</strong>
                                    <small>
                                        {selectedCategoryProductCount} товаров · 3D {selectedCategoryWith3D}
                                    </small>
                                </div>

                                <button
                                    type="button"
                                    className="catalogReview__categoryPickerToggle"
                                    aria-expanded={categoryPickerOpen}
                                    onClick={() => setSharedCategoryPickerOpen(!categoryPickerOpen)}
                                >
                                    {categoryPickerOpen ? "Скрыть категории" : "Сменить категорию"}
                                </button>
                            </div>

                            {categoryPickerOpen ? (
                                <div className="catalogReview__categoryPicker">
                                    <div className="catalogReview__categoryGrid">
                                        <button
                                            type="button"
                                            className={[
                                                "catalogReview__categoryCard",
                                                "catalogReview__categoryCard--all",
                                                selectedProductCategoryId === ALL_CATEGORIES_ID ? "is-active" : "",
                                            ].filter(Boolean).join(" ")}
                                            onClick={() => changeProductCategory(ALL_CATEGORIES_ID)}
                                        >
                                            <CategoryThumb all />
                                            <span className="catalogReview__categoryCardText">
                                                <strong>Все товары</strong>
                                                <small>
                                                    {allProductsCount} товаров · 3D {allProductsWith3D}
                                                </small>
                                            </span>
                                        </button>

                                        {sortedProductCategories.map((category) => (
                                            <button
                                                type="button"
                                                className={[
                                                    "catalogReview__categoryCard",
                                                    String(category._id) === String(selectedProductCategoryId)
                                                        ? "is-active"
                                                        : "",
                                                ].filter(Boolean).join(" ")}
                                                key={category._id}
                                                onClick={() => changeProductCategory(category._id)}
                                            >
                                                <CategoryThumb category={category} />
                                                <span className="catalogReview__categoryCardText">
                                                    <strong>{category.name}</strong>
                                                    <small>
                                                        {category.productCount || 0} товаров · 3D {category.with3D || 0}
                                                    </small>
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                        </section>

                        <nav className="catalogReview__tabs">
                            {TABS.map((tab) => (
                                <button
                                    type="button"
                                    key={tab.value}
                                    className={activeTab === tab.value ? "is-active" : ""}
                                    onClick={() => changeTab(tab.value)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                    </>
                )}

                {status ? (
                    <div className={`catalogReview__message catalogReview__message--${status.type}`}>
                        {status.text}
                    </div>
                ) : null}

                {hasCategoryContext && activeTab === "matching" ? (
                    <MatchingWorkspace
                        bootstrap={bootstrap}
                        oldList={oldList}
                        oldPagination={oldPagination}
                        oldFilters={oldFilters}
                        setOldFilters={setSharedOldFilters}
                        currentOld={currentOld}
                        purchasedCandidates={purchasedCandidates}
                        selectedPurchased={selectedPurchased}
                        loading={loading || Boolean(
                            operations[`matching:save:${currentOld?.item?.oldNumber || 0}`]
                            || operations[`matching:merge:${currentOld?.item?.oldNumber || 0}`]
                            || operations[`matching:remove:${currentOld?.item?.oldNumber || 0}`]
                        )}
                        onSelectOld={selectOldShared}
                        onLoadOldPage={(page) => setSharedOldFilters((prev) => ({ ...prev, page }))}
                        onSelectPurchased={selectPurchased}
                        onSaveMatch={saveMatch}
                        onMergeDuplicate={mergeDuplicate}
                        onRemoveDuplicate={removeDuplicate}
                        duplicateInput={duplicateInput}
                        setDuplicateInput={setDuplicateInput}
                        compare={compare}
                        sharedDraft={workspace?.matchingDraft}
                        remoteDraftPatch={remoteDraftPatch}
                        sharedCompare={workspace?.compare}
                        onDraftChange={updateMatchingDraft}
                        onOpenCompare={openCompareShared}
                        onCloseCompare={closeCompareShared}
                        onCompareViewChange={updateCompareView}
                    />
                ) : null}


                {hasCategoryContext && activeTab === "import" ? (
                    <CatalogExtensionPanel
                        categories={productCategories}
                        categoryId={selectedProductCategoryId}
                        onRefresh={() => refreshAllSummary({ refreshExtension: false })}
                        refreshToken={catalogExtensionRefreshToken}
                        sharedDraft={{
                            ...(workspace?.importDraft || {}),
                            categoryId: workspace?.importDraft?.categoryId
                                || selectedProductCategoryId,
                        }}
                        onDraftChange={publishCatalogImportDraft}
                        onOpen3D={openCatalogProduct3D}
                        onOpenPricing={openPricingFromCatalog}
                        onRealtimeChange={publishRealtimeChange}
                        realtimeOperations={operations}
                        onRealtimeOperation={publishOperation}
                    />
                ) : null}
            </div>
        </Layout>
    );
};

export default CatalogReviewPage;
